import classNames from 'classnames';
import {
  SyntheticEvent, useEffect, useRef, useState,
} from 'react';
import { IImage, TeamGridItem } from '@sapientpro/sapientpro-data-models';
import { useAtomValue } from 'jotai';
import styles from './TeamValues.module.scss';
import { useOutsideClick } from '../../useOutsideClick';
import Img from '../Img';
import VideoControlsOverlay from '../VideoControlsOverlay/VideoControlsOverlay';
import deviceScreenSize, { ScreenSize } from '../../store/deviceScreenSize';
import useInViewport from '../../useInViewport';

interface IReviewOverlayProps {
  item: TeamGridItem;
  close: () => void;
  isMobile?: boolean;
  preview?: IImage;
  currentItemId?: number;
  activeItemId?: number;
  handlePlayStart?: () => void;
}

const ReviewOverlay = ({
  item,
  close,
  isMobile,
  preview,
  activeItemId,
  currentItemId,
  handlePlayStart,
}: IReviewOverlayProps) => {
  const screenSize = useAtomValue(deviceScreenSize);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isSpeededUp, setIsSpeededUp] = useState<boolean>(false);
  const [hidePreview, setHidePreview] = useState<boolean>(false);
  const [overlayWidth, setOverlayWidth] = useState<number>(0);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLProgressElement>(null);

  const isVideoInOfView = useInViewport(videoWrapperRef, 0.3);

  useOutsideClick(videoWrapperRef, close);

  const handleResize = () => {
    const card = document.querySelector(`.${styles.teamValuesItem}`);
    const itemWidth = card?.clientWidth || 0;
    const width = itemWidth * 2 + 8;

    setOverlayWidth(width);
  };

  const togglePlay = () => {
    const isPaused = !!videoRef.current?.paused;

    if (!isPaused) {
      videoRef.current?.pause();
    } else {
      videoRef.current?.play();

      setHidePreview(true);
      handlePlayStart?.();
    }

    if (!videoRef.current) setHidePreview(true);

    setIsPlaying(!isPlaying);
  };

  const handleViewportChange = () => {
    const isVideoPlaying = !videoRef.current?.paused;
    if (isVideoPlaying) {
      const videoRect = videoWrapperRef.current?.getBoundingClientRect();

      if (videoRect) {
        const windowHeight = window.innerHeight;
        const videoHeight = videoRect.height;

        const bottomSideOutOfView = videoRect.top + videoHeight < 0;
        const topSideOutOfView = videoRect.top > windowHeight;
        const isVerticallyOutOfView = topSideOutOfView || bottomSideOutOfView;

        const isOutOfView = isVerticallyOutOfView;

        if (isOutOfView) {
          videoRef.current?.pause();
        }
      }
    }
  };

  const toggleSpeed = () => {
    const video = videoRef.current;
    setIsSpeededUp(!isSpeededUp);

    if (video) {
      if (isSpeededUp) {
        video.playbackRate = 1;
      } else {
        video.playbackRate = 2;
      }
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleClose = (e?: SyntheticEvent) => {
    e?.stopPropagation();
    e?.preventDefault();

    close();
  };

  useEffect(() => {
    if (currentItemId && activeItemId) {
      if (currentItemId !== activeItemId && isPlaying) togglePlay();
    } else if (!activeItemId && isPlaying) togglePlay();
  }, [activeItemId]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleViewportChange);
    handleResize();

    () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleViewportChange);
    };
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      const isVideoPlaying = !videoRef.current?.paused;

      if (!isVideoInOfView && isVideoPlaying) {
        togglePlay();
      }
    }
  }, [isVideoInOfView]);

  const isMobileScreen = screenSize < ScreenSize.TABLET_PORTRAIT;
  const firstName = item?.name?.split(' ')?.[0] || '';
  const videoStyle = isMobileScreen ? {} : {
    width: `${overlayWidth}px`,
    maxWidth: `${overlayWidth}px`,
  };

  const videoWrapperStyle = isMobileScreen ? {} : {
    width: `${overlayWidth}px`,
    maxWidth: `${overlayWidth}px`,
  };

  const showVideoOnMobile = isMobile ? isVideoInOfView : true;
  const showVideo = showVideoOnMobile || hidePreview;

  return (
    <div
      className={classNames(styles.reviewOverlay, {
        [styles.mobile]: isMobile,
      })}
    >
      <div
        className={styles.videoWrapper}
        ref={videoWrapperRef}
        style={videoWrapperStyle}
      >
        {showVideo && (
        <video
          className={styles.video}
          autoPlay={!isMobile}
          muted={isMuted}
          onLoadedMetadata={() => {
            const duration = videoRef.current?.duration;

            progressRef.current?.setAttribute('max', duration?.toString() || '1');
          }}
          onTimeUpdate={() => {
            const time = videoRef.current?.currentTime;

            progressRef.current?.setAttribute('value', time?.toString() || '0');
          }}
          onPlay={() => {
            setIsPlaying(true);
          }}
          onPause={() => {
            setIsPlaying(false);
          }}
          ref={videoRef}
          style={videoStyle}
          playsInline
        >
          <source
            src={item.video.url}
            type='video/mp4'
          />
          <track kind='captions' />
        </video>
        )}
        {!(!!preview && !hidePreview) && (
        <VideoControlsOverlay
          handleClose={handleClose}
          toggleMute={toggleMute}
          togglePlay={togglePlay}
          toggleSpeed={toggleSpeed}
          isMuted={isMuted}
          isPlaying={isPlaying}
          isSpeededUp={isSpeededUp}
          videoRef={videoRef}
        />
        )}
        {!!preview && !hidePreview && (
        <button
          type='button'
          className={styles.previewWrapper}
          onClick={togglePlay}
        >
          <Img
            src={preview.url}
            width={preview.width}
            height={preview.height}
            alt={preview.alternativeText || 'image'}
            className={styles.previewImage}
          />
          <svg className={styles.previewPlayIcon}>
            <use
              href='/media/play-white.svg#playWhiteSVG'
              xlinkHref='/media/play-white.svg#playWhiteSVG'
            />
          </svg>
        </button>
        )}
        <div className={styles.personData}>
          <p className={classNames(styles.name, 'header3')}>{firstName}</p>
          <p className={classNames(styles.position, 'bodyLarge')}>{`${item.position}`}</p>
        </div>
      </div>
      <div className={styles.personData}>
        <p className={classNames(styles.name, 'header3')}>{item.name}</p>
        <p className={classNames(styles.position, 'bodyLarge')}>{`${item.position}`}</p>
      </div>
    </div>
  );
};

export default ReviewOverlay;
